import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, Clock, Info, Minus, Pause, Play, Plus, Search, Trash2, XCircle } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Pagination } from '@/components/pagination';
import { PageHeader, Segmented } from '@/components/page';
import {
  AppMark,
  ChoiceCard,
  ConfirmDialog,
  EmptyState,
  GrantPill,
  KV,
  Tag,
  fmtDate,
  grantStateOf,
  initialsOf,
  type GrantState,
} from '@/components/products/shared';
import { cn } from '@/lib/utils';
import {
  useAccessMatrix,
  useGrantProductAccess,
  useProductRoles,
  useRevokeProductAccess,
  useSetProductAccessActive,
  useToast,
  useUpdateProductAccess,
  useDebouncedValue,
} from '@/hooks';
import type { AccessMatrixUser } from '@/api/products';
import type { Product, ProductAccess } from '@/types';

type CellState = GrantState | 'none';

const NO_ORG = '__none__';
const orgKey = (u: AccessMatrixUser) => u.organization?.id ?? NO_ORG;
const orgLabel = (u: AccessMatrixUser) => u.organization?.name ?? 'No organization';
const userName = (u: AccessMatrixUser) => u.name || u.email;

const CELL: Record<
  CellState,
  { icon: React.ComponentType<{ className?: string }>; label: string; cls: string }
> = {
  active: { icon: Check, label: 'Active', cls: 'text-success' },
  expiring: { icon: Clock, label: 'Expiring soon', cls: 'text-warning' },
  expired: { icon: XCircle, label: 'Expired', cls: 'text-destructive' },
  suspended: { icon: Pause, label: 'Suspended', cls: 'text-warning' },
  none: { icon: Minus, label: 'No access', cls: 'text-muted-foreground/60' },
};

/**
 * Admin overview: a users × apps grid. Each cell shows a user's access state for
 * an app; selecting it opens a sheet to grant, change, suspend, resume or revoke.
 */
export function AccessMatrixPage() {
  const [userQuery, setUserQuery] = useState('');
  const [orgFilter, setOrgFilter] = useState('all');
  const [appsShown, setAppsShown] = useState<'active' | 'all'>('active');
  const [page, setPage] = useState(1);
  const [sheet, setSheet] = useState<{ user: AccessMatrixUser; product: Product } | null>(null);
  const debouncedQuery = useDebouncedValue(userQuery);
  const { data: matrix, isLoading, isError, refetch } = useAccessMatrix({
    page,
    pageSize: 25,
    search: debouncedQuery || undefined,
    organizationId: orgFilter === 'all' ? undefined : orgFilter,
  });
  const users = matrix?.users ?? [];
  const grants = matrix?.grants ?? [];

  const grantByKey = useMemo(() => {
    const map = new Map<string, ProductAccess>();
    for (const access of grants) map.set(`${access.user_id}::${access.product_id}`, access);
    return map;
  }, [grants]);

  const organizations = useMemo(() => {
    const entries = (matrix?.organizations ?? []).map((o) => [o.id, o.name] as const);
    return [...entries].sort((a, b) => a[1].localeCompare(b[1]));
  }, [matrix?.organizations]);

  const products = useMemo(
    () =>
      [...(matrix?.products ?? [])]
        .filter((p) => appsShown === 'all' || p.is_active)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [matrix?.products, appsShown]
  );

  const userGroups = useMemo(() => {
    const groups = new Map<string, { label: string; orgId?: string; users: AccessMatrixUser[] }>();
    for (const u of users) {
      const key = orgKey(u);
      if (!groups.has(key)) groups.set(key, { label: orgLabel(u), orgId: u.organization?.id, users: [] });
      groups.get(key)!.users.push(u);
    }
    return [...groups.entries()].sort((a, b) =>
      a[0] === NO_ORG ? 1 : b[0] === NO_ORG ? -1 : a[1].label.localeCompare(b[1].label)
    );
  }, [users]);

  const stateOf = (userId: string, productId: string): CellState => {
    const g = grantByKey.get(`${userId}::${productId}`);
    return g ? grantStateOf(g) : 'none';
  };

  const clearFilters = () => {
    setUserQuery('');
    setOrgFilter('all');
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        title="Access matrix"
        description="Who can sign into which app. Select a cell to grant, change, suspend or revoke access."
      />

      <Card className="overflow-hidden" aria-label="Access by user and app">
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-[18px] py-3">
          <div className="relative min-w-[200px] flex-1 sm:max-w-[260px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              aria-label="Search users"
              placeholder="Search name or email"
              autoComplete="off"
              className="pl-9"
              value={userQuery}
              onChange={(e) => {
                setUserQuery(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <Select
            value={orgFilter}
            onValueChange={(v) => {
              setOrgFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger aria-label="Organization" className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All organizations</SelectItem>
              {organizations.map(([id, name]) => (
                <SelectItem key={id} value={id}>
                  {name}
                </SelectItem>
              ))}
              <SelectItem value={NO_ORG}>No organization</SelectItem>
            </SelectContent>
          </Select>
          <Segmented<'active' | 'all'>
            value={appsShown}
            onChange={setAppsShown}
            options={[
              { value: 'active', label: 'Active apps' },
              { value: 'all', label: 'All apps' },
            ]}
          />
          <span className="flex-1" />
          <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs text-muted-foreground" aria-label="Legend">
            {(['active', 'expiring', 'suspended', 'expired', 'none'] as CellState[]).map((s) => {
              const c = CELL[s];
              return (
                <span key={s} className="inline-flex items-center gap-1">
                  <c.icon className={cn('h-3.5 w-3.5', c.cls)} />
                  {s === 'expiring' ? 'Expiring' : c.label.replace('No access', 'None')}
                </span>
              );
            })}
          </div>
        </div>

        {isLoading ? (
          <div className="h-64 animate-pulse bg-foreground/[0.03]" aria-busy="true" aria-label="Loading access matrix" />
        ) : isError ? (
          <EmptyState
            icon={AlertTriangle}
            title="Couldn’t load the access matrix"
            action={
              <Button variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : users.length === 0 ? (
          <EmptyState
            icon={Search}
            title="No users match"
            body="Try another name, or show all organizations."
            action={
              <Button variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          />
        ) : products.length === 0 ? (
          <EmptyState
            icon={Search}
            title="No apps to show"
            body="Create an app, or switch to all apps."
            action={
              <Button variant="outline" asChild>
                <Link to="/products">Go to apps</Link>
              </Button>
            }
          />
        ) : (
          <div className="relative max-h-[calc(100vh-290px)] min-h-[320px] overflow-auto max-md:max-h-none">
            <table className="min-w-full border-separate border-spacing-0 text-[13px]">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="sticky left-0 top-0 z-30 min-w-[180px] border-b border-r border-border bg-card px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground md:min-w-[240px]"
                  >
                    User
                  </th>
                  {products.map((p) => (
                    <th
                      key={p.id}
                      scope="col"
                      className="sticky top-0 z-20 w-[96px] min-w-[96px] border-b border-border bg-card px-1.5 py-2.5 text-center align-bottom text-xs font-semibold"
                    >
                      <Link
                        to={`/products/${p.id}`}
                        className={cn(
                          'mx-auto block max-w-[88px] truncate hover:underline',
                          !p.is_active && 'text-muted-foreground'
                        )}
                        title={`${p.name}${p.organization_name ? ` — ${p.organization_name}` : ''}${p.is_active ? '' : ' (inactive)'}`}
                      >
                        {p.name}
                      </Link>
                      {!p.is_active && (
                        <span className="block text-[10px] font-normal text-muted-foreground">inactive</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              {userGroups.map(([key, grp]) => (
                <tbody key={key}>
                  <tr>
                    <th
                      colSpan={1 + products.length}
                      scope="colgroup"
                      className="sticky left-0 z-10 border-b border-border bg-foreground/[0.04] px-3 py-1.5 text-left font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground"
                    >
                      {grp.orgId ? (
                        <Link to={`/organizations/${grp.orgId}`} className="hover:underline">
                          {grp.label}
                        </Link>
                      ) : (
                        grp.label
                      )}{' '}
                      · {grp.users.length}
                    </th>
                  </tr>
                  {grp.users.map((u) => (
                    <tr key={u.id} className="group">
                      <th
                        scope="row"
                        className="sticky left-0 z-10 border-b border-r border-border bg-card px-3 py-1.5 text-left font-normal group-hover:bg-muted"
                      >
                        <Link to={`/users/${u.id}`} className="flex min-w-0 items-center gap-2.5">
                          <Avatar className="h-7 w-7 shrink-0 border">
                            {u.picture && <AvatarImage src={u.picture} alt="" />}
                            <AvatarFallback className="text-[10px] font-medium">
                              {initialsOf(u.name, u.email)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="min-w-0">
                            <b className="block max-w-[190px] truncate font-semibold" title={userName(u)}>
                              {userName(u)}
                            </b>
                            {u.name && (
                              <span
                                className="block max-w-[190px] truncate text-xs text-muted-foreground"
                                title={u.email}
                              >
                                {u.email}
                              </span>
                            )}
                          </span>
                        </Link>
                      </th>
                      {products.map((p) => {
                        const st = stateOf(u.id, p.id);
                        const c = CELL[st];
                        const roles = grantByKey.get(`${u.id}::${p.id}`)?.roles.join(', ');
                        return (
                          <td key={p.id} className="border-b border-border p-0 text-center group-hover:bg-foreground/[0.03]">
                            <button
                              type="button"
                              onClick={() => setSheet({ user: u, product: p })}
                              aria-label={`${userName(u)} — ${p.name}: ${c.label}${roles ? ` (${roles})` : ''}`}
                              title={`${c.label}${roles ? ` · ${roles}` : ''}`}
                              className="mx-auto flex h-11 w-full min-w-11 items-center justify-center hover:bg-foreground/[0.06] focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-ring"
                            >
                              <c.icon className={cn('h-4 w-4', c.cls)} />
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )}

        {matrix?.pagination && (
          <Pagination
            className="border-t px-[18px] pb-4"
            pagination={matrix.pagination}
            onPageChange={setPage}
            itemLabel="users"
          />
        )}
      </Card>

      {sheet && (
        <CellSheet
          key={`${sheet.user.id}-${sheet.product.id}`}
          user={sheet.user}
          product={sheet.product}
          grant={grantByKey.get(`${sheet.user.id}::${sheet.product.id}`)}
          grantedByName={(() => {
            const by = grantByKey.get(`${sheet.user.id}::${sheet.product.id}`)?.granted_by;
            const hit = by ? users.find((u) => u.id === by) : undefined;
            return hit ? userName(hit) : null;
          })()}
          onClose={() => setSheet(null)}
        />
      )}
    </div>
  );
}

/* ── cell sheet ──────────────────────────────────────── */

type ExpiryChoice = 'keep' | 'never' | '7' | '30' | '90';

function CellSheet({
  user,
  product,
  grant,
  grantedByName,
  onClose,
}: {
  user: AccessMatrixUser;
  product: Product;
  grant?: ProductAccess;
  grantedByName: string | null;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const { data: catalog = [] } = useProductRoles(product.id);
  const grantAccess = useGrantProductAccess();
  const updateAccess = useUpdateProductAccess();
  const setActive = useSetProductAccessActive();
  const revokeAccess = useRevokeProductAccess();

  const state: CellState = grant ? grantStateOf(grant) : 'none';
  const [picked, setPicked] = useState<string[]>(grant?.roles ?? []);
  const [expiry, setExpiry] = useState<ExpiryChoice>(grant ? 'keep' : 'never');
  const [confirm, setConfirm] = useState<'suspend' | 'revoke' | null>(null);

  const model = product.settings?.access_model ?? 'gated';
  const blurb =
    model === 'open'
      ? `${product.name} is open to every signed-in user. A grant only adds role claims.`
      : model === 'org_members'
        ? `Members of the organization get access automatically${
            product.settings?.default_role ? ` with the ${product.settings.default_role} role` : ''
          }. A grant adds roles or access for people outside it.`
        : `${product.name} is gated: only people with an active grant can sign in.`;

  const busy =
    grantAccess.isPending || updateAccess.isPending || setActive.isPending || revokeAccess.isPending;

  const expiresIso = (): string | null | undefined => {
    if (expiry === 'keep') return grant?.expires_at ?? null;
    if (expiry === 'never') return null;
    return new Date(Date.now() + Number(expiry) * 864e5).toISOString();
  };

  const fail = (title: string, error: unknown) =>
    toast({
      variant: 'destructive',
      title,
      description: error instanceof Error ? error.message : 'Something went wrong',
    });

  const doGrant = async () => {
    try {
      await grantAccess.mutateAsync({
        productId: product.id,
        data: { user_id: user.id, roles: picked, expires_at: expiresIso() ?? undefined },
      });
      toast({ title: 'Access granted', description: `${userName(user)} → ${product.name}` });
      onClose();
    } catch (error) {
      fail('Grant failed', error);
    }
  };
  const doSave = async () => {
    try {
      await updateAccess.mutateAsync({
        productId: product.id,
        userId: user.id,
        data: { roles: picked, expires_at: expiresIso() ?? null },
      });
      toast({ title: 'Access updated', description: `${userName(user)} → ${product.name}` });
      onClose();
    } catch (error) {
      fail('Update failed', error);
    }
  };
  const doActive = async (isActive: boolean) => {
    try {
      await setActive.mutateAsync({ productId: product.id, userId: user.id, isActive });
      toast({
        title: isActive ? 'Access resumed' : 'Access suspended',
        description: `${userName(user)} → ${product.name}`,
      });
      setConfirm(null);
      onClose();
    } catch (error) {
      fail(isActive ? 'Resume failed' : 'Suspend failed', error);
    }
  };
  const doRevoke = async () => {
    try {
      await revokeAccess.mutateAsync({ productId: product.id, userId: user.id });
      toast({ title: 'Access revoked', description: `${userName(user)} → ${product.name}` });
      setConfirm(null);
      onClose();
    } catch (error) {
      fail('Revoke failed', error);
    }
  };

  return (
    <>
      <Dialog open={confirm === null} onOpenChange={(o) => !o && !busy && onClose()}>
        <DialogContent className="max-w-lg">
          <div className="flex items-center gap-3 pr-8">
            <AppMark name={product.name} />
            <div className="min-w-0">
              <DialogTitle className="truncate text-lg">{product.name}</DialogTitle>
              <DialogDescription className="truncate">
                {userName(user)} · {user.email}
              </DialogDescription>
            </div>
          </div>

          <div className="flex gap-3 rounded-lg border border-border bg-foreground/[0.03] p-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-[13px]">{blurb}</p>
          </div>

          <KV
            rows={[
              {
                label: 'State',
                value:
                  state === 'none' ? (
                    <Tag solid>No grant</Tag>
                  ) : (
                    <GrantPill state={state} />
                  ),
              },
              ...(grant
                ? [
                    {
                      label: 'Granted',
                      value: (
                        <span className="font-mono text-xs tabular-nums">
                          {fmtDate(grant.granted_at)}
                          {grantedByName && ` by ${grantedByName}`}
                        </span>
                      ),
                    },
                    {
                      label: 'Expires',
                      value: (
                        <span className="font-mono text-xs tabular-nums">
                          {grant.expires_at ? fmtDate(grant.expires_at) : 'Never'}
                        </span>
                      ),
                    },
                    ...(grant.permissions.length
                      ? [
                          {
                            label: 'Extra permissions',
                            value: (
                              <div className="flex flex-wrap gap-1.5">
                                {grant.permissions.map((x) => (
                                  <Tag key={x} solid>
                                    {x}
                                  </Tag>
                                ))}
                              </div>
                            ),
                          },
                        ]
                      : []),
                  ]
                : []),
              {
                label: 'App',
                value: (
                  <Link to={`/products/${product.id}`} className="text-primary hover:underline">
                    Open {product.name}
                  </Link>
                ),
              },
              {
                label: 'User',
                value: (
                  <Link to={`/users/${user.id}`} className="text-primary hover:underline">
                    Open {userName(user)}
                  </Link>
                ),
              },
            ]}
          />

          <fieldset className="space-y-2">
            <legend className="mb-1 text-[13px] font-medium">Roles</legend>
            {catalog.length ? (
              <div className="grid max-h-48 gap-2 overflow-y-auto sm:grid-cols-2">
                {catalog.map((r) => (
                  <ChoiceCard
                    key={r.name}
                    type="checkbox"
                    mono
                    value={r.name}
                    checked={picked.includes(r.name)}
                    onChange={(c) =>
                      setPicked((p) => (c ? [...p, r.name] : p.filter((x) => x !== r.name)))
                    }
                    title={r.name}
                    description={r.description || 'No description'}
                  />
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                No roles defined for this app. Access is sign-in only.
              </p>
            )}
          </fieldset>

          <div className="space-y-2">
            <Label htmlFor="sheet-exp">Expires</Label>
            <Select value={expiry} onValueChange={(v) => setExpiry(v as ExpiryChoice)}>
              <SelectTrigger id="sheet-exp" className="sm:max-w-[240px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {grant && <SelectItem value="keep">Keep current expiry</SelectItem>}
                <SelectItem value="never">Never</SelectItem>
                <SelectItem value="7">In 7 days</SelectItem>
                <SelectItem value="30">In 30 days</SelectItem>
                <SelectItem value="90">In 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
            {grant ? (
              <>
                <Button
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  disabled={busy}
                  onClick={() => setConfirm('revoke')}
                >
                  <Trash2 className="h-4 w-4" />
                  Revoke
                </Button>
                <span className="flex-1" />
                {grant.is_active ? (
                  <Button variant="outline" disabled={busy} onClick={() => setConfirm('suspend')}>
                    <Pause className="h-4 w-4" />
                    Suspend
                  </Button>
                ) : (
                  <Button variant="outline" disabled={busy} onClick={() => doActive(true)}>
                    <Play className="h-4 w-4" />
                    Resume
                  </Button>
                )}
                <Button disabled={busy} onClick={doSave}>
                  {updateAccess.isPending ? 'Saving…' : 'Save changes'}
                </Button>
              </>
            ) : (
              <>
                <span className="flex-1" />
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button disabled={busy} onClick={doGrant}>
                  <Plus className="h-4 w-4" />
                  {grantAccess.isPending ? 'Granting…' : 'Grant access'}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirm === 'suspend'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Suspend access?"
        body={`${userName(user)} can’t sign into ${product.name} or refresh tokens until you resume. Roles are kept.`}
        confirmLabel="Suspend access"
        pendingLabel="Suspending…"
        danger
        pending={setActive.isPending}
        onConfirm={() => doActive(false)}
      />
      <ConfirmDialog
        open={confirm === 'revoke'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Revoke access?"
        body={`${userName(user)} loses access to ${product.name} and their refresh tokens for it are revoked. Restoring it needs a new grant.`}
        confirmLabel="Revoke access"
        pendingLabel="Revoking…"
        danger
        pending={revokeAccess.isPending}
        onConfirm={doRevoke}
      />
    </>
  );
}
